import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import endOfWeek from 'date-fns/endOfWeek'
import startOfWeek from 'date-fns/startOfWeek'
import { noop, get } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useGetServiceEventsMutation } from 'api/configured-service/getServiceEvents'
import { useLazySearchConfiguredServicesQuery } from 'api/configured-service/searchConfiguredServices'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import CombinedNotesDialog from 'components/notes/combined/CombinedNotesDialog'
import { canUpdateService } from 'data/permissions/permissionsSelectors'
import { putIsLoading } from 'middleware/actions/response'
import { NOTE_TYPES } from 'settings/constants/note'
import { CONFIGURED_SERVICES_PAGINATION } from 'settings/constants/pagination'
import T from 'T'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { getAPIIdParam } from 'utils/form-body'
import { memo } from 'utils/react'

import EventsDrawer from '../drawer/events'

import ConfiguredServiceActionMenu from './ConfiguredServices/ConfiguredServiceActionMenu'
import Header from './ConfiguredServices/Header'
import ConfiguredServicesRow from './ConfiguredServices/ServiceCard/ServiceDetailsCard'
import CustomerDetailsPaginations from './CustomerDetailsPaginations'
import { Accordion, AccordionSummary, AccordionDetails } from './StyledAccordian'

import './common.scss'

const HHSectionPlaceholderUntyped = HHSectionPlaceholder as React.ElementType
const EventsDrawerUntyped = EventsDrawer as React.ElementType
const ConfiguredServicesRowUntyped = ConfiguredServicesRow as React.ElementType
const CustomerDetailsPaginationsUntyped = CustomerDetailsPaginations as React.ElementType

const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CONFIGURED_SERVICES_PAGINATION

type ConfiguredService = { id?: string; deactivate?: boolean } & Record<string, unknown>

type ServiceRowProps = {
  service: ConfiguredService
  selectedServiceId?: string
  onAddEvent: (service: ConfiguredService) => void
  onOpenActionMenu: (event: React.MouseEvent<HTMLElement>, service: ConfiguredService) => void
  onOpenNotes: (service: ConfiguredService) => void
  onSaveTags: (data: unknown, closeCallback: () => void) => void
}

const ServiceRow = ({ service, selectedServiceId, onAddEvent, onOpenActionMenu, onOpenNotes, onSaveTags }: ServiceRowProps) => {
  const handleAddEvent = useCallback(() => onAddEvent(service), [onAddEvent, service])
  const handleActionClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => onOpenActionMenu(event, service),
    [onOpenActionMenu, service]
  )
  const handleViewNotes = useCallback(() => onOpenNotes(service), [onOpenNotes, service])

  return (
    <ConfiguredServicesRowUntyped
      selectedServiceId={selectedServiceId}
      service={service}
      onActionClick={handleActionClick}
      onAddEventClick={handleAddEvent}
      onSaveTags={onSaveTags}
      onViewNotesClick={handleViewNotes}
    />
  )
}

type ConfiguredServicesProps = {
  accountId: string
  locationId?: string
}

const ConfiguredServices = ({ accountId, locationId = '' }: ConfiguredServicesProps) => {
  const methods = useForm({
    defaultValues: {
      startDate: new Date(),
      upcoming: false,
      status: ['Active'],
      accountId,
      locationId,
      page: INITIAL_PAGE,
      size: ROWS_PER_PAGE,
    },
  })
  const dispatch = useDispatch()
  const updateService = useSelector(canUpdateService, shallowEqual)
  const [currentServiceId, setCurrentServiceId] = useState<string | null>(null)
  const [isOpenEventsDrawer, setIsOpenEventsDrawer] = useState(false)
  const [isOpenActionMenu, setIsOpenActionMenu] = useState<HTMLElement | false>(false)
  const [selectedService, setSelectedService] = useState<ConfiguredService | null>(null)
  const [isOpenCSNotesDialog, setIsOpenCSNotesDialog] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [page, setPage] = useState(INITIAL_PAGE)
  const [size, setSize] = useState(ROWS_PER_PAGE)
  const [searchConfiguredServices, { isFetching, isLoading, data }] = useLazySearchConfiguredServicesQuery()
  const [getServiceEvents, { data: eventsData }] = useGetServiceEventsMutation()
  const { getValues, watch } = methods
  const selectedServiceId = currentServiceId

  const fetchConfiguredServices = useCallback(() => {
    const { status, upcoming, startDate } = getValues()
    return searchConfiguredServices({
      ...getAPIIdParam(locationId, accountId),
      page,
      size,
      status: status.join(','),
      upcoming,
      startDate: formatDateToBEFormatDateFns(upcoming ? new Date() : startOfWeek(startDate)),
      endDate: upcoming ? null : formatDateToBEFormatDateFns(endOfWeek(startDate)),
    }).unwrap()
  }, [accountId, getValues, locationId, page, searchConfiguredServices, size])

  const handleAddEventClick = useCallback(
    (service: ConfiguredService) => {
      if (service.deactivate) return

      dispatch(putIsLoading(true))
      getServiceEvents({ configuredServiceId: service.id })
        .unwrap()
        .then(() => {
          setCurrentServiceId(service.id ?? null)
          setIsOpenEventsDrawer(true)
        })
        .catch(handleError)
        .finally(() => dispatch(putIsLoading(false)))
    },
    [dispatch, getServiceEvents]
  )

  const handleSaveTags = useCallback(
    (_data: unknown, closeCallback: () => void) => {
      closeCallback()
      fetchConfiguredServices()
    },
    [fetchConfiguredServices]
  )

  const handleOpenActionMenu = useCallback((event: React.MouseEvent<HTMLElement>, service: ConfiguredService) => {
    setIsOpenActionMenu(event.currentTarget)
    setSelectedService(service)
  }, [])

  const handleCloseActionMenu = useCallback(() => setIsOpenActionMenu(false), [])

  const handleOpenCSNotesDialog = useCallback((service: ConfiguredService) => {
    setSelectedService(service)
    setIsOpenCSNotesDialog(true)
  }, [])

  const handleCloseCSNotesDialog = useCallback(() => {
    setIsOpenCSNotesDialog(false)
    setSelectedService(null)
  }, [])

  const handleToggleExpanded = useCallback(() => setExpanded(current => !current), [])
  const handlePageChange = useCallback((newPage: number) => setPage(newPage), [])
  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setSize(Number(event.target.value)), [])
  const handleCloseEventsDrawer = useCallback(() => {
    setIsOpenEventsDrawer(false)
    setCurrentServiceId(null)
  }, [])

  useEffect(() => {
    const subscription = watch(() => {
      fetchConfiguredServices()
    })
    return () => subscription.unsubscribe()
  }, [watch, locationId, accountId, dispatch, fetchConfiguredServices])

  useEffect(() => {
    if (locationId || accountId) {
      fetchConfiguredServices()
    }
  }, [accountId, locationId, page, size, fetchConfiguredServices])

  const totalCount = get(data, 'totalItems', 0)
  const totalPages = get(data, 'totalPages', 0)
  return (
    <FormProvider {...methods}>
      <Accordion expanded={expanded} onChange={noop}>
        <AccordionSummary
          disablePointer
          withNewColors
          expandIcon={null}
          sx={{ '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': { mx: 0 } }}
        >
          <Header isExpanded={expanded} label={`${T.CONFIGURED_SERVICE}s`} onLabelClick={handleToggleExpanded} />
        </AccordionSummary>
        <AccordionDetails>
          {!isLoading && !isFetching && Array.isArray(data?.services) && data.services.length === 0 && (
            <HHSectionPlaceholderUntyped
              title={!watch('status')?.includes('Deactivated') ? T.NO_CONFIGURED_SERVICES : T.NO_DEACTIVATED_CONFIGURED_SERVICES}
            />
          )}

          {!isLoading && !isFetching && Array.isArray(data?.services) && data.services.length > 0 && (
            <Box px={2} py={1} width="100%">
              <Stack spacing={1} sx={{ minWidth: 0 }}>
                {(data.services as ConfiguredService[]).map(service => (
                  <ServiceRow
                    key={service.id}
                    selectedServiceId={selectedServiceId ?? undefined}
                    service={service}
                    onAddEvent={handleAddEventClick}
                    onOpenActionMenu={handleOpenActionMenu}
                    onOpenNotes={handleOpenCSNotesDialog}
                    onSaveTags={handleSaveTags}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {totalCount > ROWS_PER_PAGE && (
            <CustomerDetailsPaginationsUntyped
              withNewColors
              handlePageChange={handlePageChange}
              handleRowsPerPageChange={handleRowsPerPageChange}
              page={page}
              rowsPerPage={size}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              totalCount={totalCount}
              totalPageCount={totalPages}
            />
          )}
        </AccordionDetails>
        {updateService && (
          <EventsDrawerUntyped
            configuredServiceId={currentServiceId}
            eventInstances={eventsData}
            isOpenDrawer={isOpenEventsDrawer}
            onClose={handleCloseEventsDrawer}
            onRefetch={fetchConfiguredServices}
          />
        )}
      </Accordion>
      <ConfiguredServiceActionMenu anchorEl={isOpenActionMenu} selectedService={selectedService} onClose={handleCloseActionMenu} />
      {isOpenCSNotesDialog && (
        <CombinedNotesDialog
          open
          configuredServiceId={selectedService?.id}
          defaultTab={NOTE_TYPES.CONFIGURED_SERVICE}
          onClose={handleCloseCSNotesDialog}
        />
      )}
    </FormProvider>
  )
}

export default memo(ConfiguredServices)
